import { CircuitSchema } from '../types';

export interface SchemaDiff {
  addedNodes: string[];
  removedNodes: string[];
  changedNodes: string[];
  addedEdges: number;
  removedEdges: number;
  scenariosBefore: number;
  scenariosAfter: number;
  /** Scenarios edited (names, ambient, overrides), whether or not the count changed. */
  scenariosChanged: boolean;
  fluidChanged: boolean;
}

const nodeName = (n: any) => n?.data?.custom_name || n?.data?.component?.name || n?.id;

/** Only what a user cares about when judging a node edit (not its position). */
const nodeKey = (n: any) => JSON.stringify({
  component: n?.data?.component?.id ?? null,
  type: n?.data?.component?.component_type,
  ports: [n?.data?.component?.input_ports, n?.data?.component?.output_ports],
  name: n?.data?.custom_name,
  properties: n?.data?.properties,
  max: n?.data?.max_temperatures,
});

const edgeKey = (e: any) =>
  `${e.source}:${e.sourceHandle || ''}>${e.target}:${e.targetHandle || ''}`;

/** Summarise how `after` differs from `before`, for the assistant's proposal card. */
export function diffSchemas(before: CircuitSchema, after: CircuitSchema): SchemaDiff {
  const hydraulic = (s: CircuitSchema) => (s.nodes || []).filter(n => n.type !== 'annotationNode');
  const beforeNodes = new Map(hydraulic(before).map(n => [n.id, n]));
  const afterNodes = new Map(hydraulic(after).map(n => [n.id, n]));

  const addedNodes: string[] = [];
  const changedNodes: string[] = [];
  afterNodes.forEach((node, id) => {
    const old = beforeNodes.get(id);
    if (!old) addedNodes.push(nodeName(node));
    else if (nodeKey(old) !== nodeKey(node)) changedNodes.push(nodeName(node));
  });
  const removedNodes: string[] = [];
  beforeNodes.forEach((node, id) => {
    if (!afterNodes.has(id)) removedNodes.push(nodeName(node));
  });

  const beforeEdges = new Set((before.edges || []).map(edgeKey));
  const afterEdges = new Set((after.edges || []).map(edgeKey));
  let addedEdges = 0;
  let removedEdges = 0;
  afterEdges.forEach(k => { if (!beforeEdges.has(k)) addedEdges += 1; });
  beforeEdges.forEach(k => { if (!afterEdges.has(k)) removedEdges += 1; });

  return {
    addedNodes,
    removedNodes,
    changedNodes,
    addedEdges,
    removedEdges,
    scenariosBefore: before.simulation_params?.scenarios?.length ?? 0,
    scenariosAfter: after.simulation_params?.scenarios?.length ?? 0,
    scenariosChanged: JSON.stringify(before.simulation_params?.scenarios ?? [])
      !== JSON.stringify(after.simulation_params?.scenarios ?? []),
    fluidChanged: (before.simulation_params?.fluid_id ?? null) !== (after.simulation_params?.fluid_id ?? null),
  };
}

export function isEmptyDiff(d: SchemaDiff): boolean {
  return !d.addedNodes.length && !d.removedNodes.length && !d.changedNodes.length
    && !d.addedEdges && !d.removedEdges && !d.scenariosChanged && !d.fluidChanged;
}
